'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import type { ProductAttribute, ProductComponent, AttributeValue } from '@config3d/contracts';
import type { ConfiguratorExperienceMode } from './configurator-mode-intro';

interface ConfigOptionsPanelProps {
  attributes: ProductAttribute[];
  components: ProductComponent[];
  selections: Record<string, unknown>;
  expandedId: string | null;
  onExpandedChange: (id: string | null) => void;
  onAttributeChange: (key: string, value: string | number | boolean) => void;
  onComponentSelect: (componentId: string, variantId: string) => void;
  onSummaryOpen: () => void;
  totalPrice: string | null;
  hasSelections: boolean;
  componentMetadata?: Record<string, Record<string, unknown> | undefined>;
  disabledOptionsByAttr?: Record<string, Set<string>>;
  /** Dynamic maxLength overrides (e.g. Engraving from Text Canvas fontMap). */
  maxLengthByAttr?: Record<string, number>;
  mobileDocked?: boolean;
  mobileCollapsed?: boolean;
  onMobileCollapseToggle?: () => void;
  desktopCollapsed?: boolean;
  onDesktopCollapseToggle?: () => void;
  experienceMode?: ConfiguratorExperienceMode;
  guidedActiveId?: string | null;
  guidedStepIndex?: number;
  guidedStepTotal?: number;
  onGuidedPrev?: () => void;
  onGuidedNext?: () => void;
  /** Hide “Configuration & export” footer link (clean / minimal chrome). */
  hideExportFooter?: boolean;
}

const SERIF = { fontFamily: 'ui-serif, Georgia, "Times New Roman", serif' } as const;

/** Readable on bright or dark 3D backgrounds: light fill + dark halo; works without theme switching. */
const MENU_LABEL =
  'text-zinc-100 [text-shadow:0_0_2px_rgba(0,0,0,.9),0_1px_3px_rgba(0,0,0,.85),0_0_12px_rgba(0,0,0,.45)]';
const MENU_MUTED =
  'text-zinc-200/90 [text-shadow:0_0_2px_rgba(0,0,0,.85),0_1px_2px_rgba(0,0,0,.75)]';
const PILL_BASE =
  'rounded-full border px-3 py-1.5 text-[11px] tracking-wide backdrop-blur-md transition-all duration-200 shadow-[0_2px_14px_rgba(0,0,0,.3)]';
const PILL_IDLE = `${PILL_BASE} border-white/25 bg-black/45 text-zinc-50 hover:border-white/40 hover:bg-black/55`;
const PILL_ON = `${PILL_BASE} border-black/25 bg-white text-zinc-900 shadow-[0_2px_16px_rgba(0,0,0,.35),inset_0_0_0_1px_rgba(0,0,0,.06)]`;

/* ------------------------------------------------------------------ */
/*  Inline controls for non-swatch attributes                         */
/* ------------------------------------------------------------------ */

function InlineSelect({ values, currentValue, onChange, disabledOptions }: {
  values: AttributeValue[];
  currentValue: string;
  onChange: (v: string) => void;
  disabledOptions?: Set<string>;
}) {
  if (values.length === 0) {
    return <p className={`text-right text-[10px] ${MENU_MUTED}`}>No choices available.</p>;
  }
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {values.map((v) => {
        const off = disabledOptions?.has(v.key);
        const sel = currentValue === v.key;
        return (
          <button
            key={v.key}
            type="button"
            onClick={() => !off && onChange(v.key)}
            disabled={off}
            className={`${sel ? PILL_ON : PILL_IDLE} ${off ? 'opacity-25 pointer-events-none' : ''}`}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

function InlineToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!on)} className="flex items-center gap-2 rounded-lg border border-white/20 bg-black/40 px-2 py-1 backdrop-blur-md shadow-[0_2px_12px_rgba(0,0,0,.25)] ms-auto">
      <span className={`relative h-[18px] w-8 shrink-0 rounded-full border border-white/15 transition-colors duration-200 ${on ? 'bg-white' : 'bg-black/50'}`}>
        <span className={`absolute top-[2px] left-[2px] h-[14px] w-[14px] rounded-full border border-black/20 shadow-sm transition-transform duration-200 ${on ? 'translate-x-[14px] bg-zinc-900' : 'bg-zinc-100'}`} />
      </span>
      <span className={`text-[10px] font-medium ${MENU_MUTED}`}>{on ? 'On' : 'Off'}</span>
    </button>
  );
}

function InlineText({
  value,
  onChange,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      maxLength={maxLength != null && maxLength >= 0 ? maxLength : undefined}
      onChange={(e) => onChange(e.target.value)}
      className={
        'ms-auto block w-full max-w-sm rounded-full border border-zinc-900/35 bg-white/[0.06] px-3 py-1.5 text-right text-[12px] outline-none transition-[border,box-shadow] ' +
        'placeholder:text-zinc-600/55 focus:border-zinc-900/55 focus:ring-1 focus:ring-zinc-900/10 ' +
        `${MENU_LABEL} dark:border-white/30 dark:bg-white/[0.06] dark:placeholder:text-zinc-400/70 dark:focus:border-white/45 dark:focus:ring-white/10`
      }
    />
  );
}

function InlineNumber({ value, min, max, step, unit, onChange }: {
  value: number; min: number; max: number; step: number; unit?: string; onChange: (v: number) => void;
}) {
  return (
    <div className="ms-auto flex w-full max-w-xs items-center justify-end gap-3">
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 flex-1 appearance-none rounded-full bg-black/40 outline-none ring-1 ring-inset ring-white/15 accent-zinc-900 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/40 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_1px_4px_rgba(0,0,0,.5)] [&::-webkit-slider-thumb]:appearance-none"
      />
      <span className={`text-[10px] tabular-nums ${MENU_MUTED}`}>{value}{unit || ''}</span>
    </div>
  );
}

/** Shopper-facing Color option. Scene rules still key off C3D `material: gradient`. */
const GRADIENT_COLOR: AttributeValue = {
  id: 'gradient',
  key: 'gradient',
  label: 'Gradient',
  color: '#debcfb',
};

function isCarpaintFamily(material: string): boolean {
  return material === 'carpaint' || material === 'gradient' || material === '';
}

function MaterialControl({
  attr,
  currentValue,
  onChange,
  disabledOptions,
}: {
  attr: ProductAttribute;
  currentValue: string;
  onChange: (key: string, v: string | number | boolean) => void;
  disabledOptions?: Set<string>;
}) {
  const values = (attr.values ?? []).filter((v) => v.key !== 'wicker' && v.key !== 'gradient');
  // Gradient is a carpaint-family color, so keep Carpaint highlighted while it is on.
  const shown = currentValue === 'gradient' ? 'carpaint' : currentValue;

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {values.map((v) => {
        const off = disabledOptions?.has(v.key);
        const sel = shown === v.key;
        return (
          <button
            key={v.key}
            type="button"
            onClick={() => {
              if (off) return;
              onChange(attr.key, v.key);
            }}
            disabled={off}
            className={`${sel ? PILL_ON : PILL_IDLE} ${off ? 'opacity-25 pointer-events-none' : ''}`}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

/** Color / gemstone pills. Gradient is shown here, written internally as `material: gradient`. */
function ColorControl({
  attr,
  gemstoneValue,
  material,
  onChange,
  disabledOptions,
}: {
  attr: ProductAttribute;
  gemstoneValue: string;
  material: string;
  onChange: (key: string, v: string | number | boolean) => void;
  disabledOptions?: Set<string>;
}) {
  const values = [...(attr.values ?? [])].filter(
    (v) => v.key !== 'gradient' || isCarpaintFamily(material),
  );
  if (isCarpaintFamily(material) && !values.some((v) => v.key === 'gradient')) {
    values.push(GRADIENT_COLOR);
  }
  const current = material === 'gradient' ? 'gradient' : gemstoneValue;

  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {values.map((v) => {
        const off = disabledOptions?.has(v.key);
        const sel = current === v.key;
        return (
          <button
            key={v.key}
            type="button"
            onClick={() => {
              if (off) return;
              onChange('color', v.key);
            }}
            disabled={off}
            className={`${sel ? PILL_ON : PILL_IDLE} ${off ? 'opacity-25 pointer-events-none' : ''}`}
          >
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

function renderControl(
  attr: ProductAttribute,
  currentValue: string,
  onChange: (key: string, v: string | number | boolean) => void,
  disabledOptions?: Set<string>,
  maxLengthOverride?: number,
  selections?: Record<string, unknown>,
) {
  const fire = (v: string | number | boolean) => onChange(attr.key, v);
  const vals = attr.values ?? [];
  const maxLength =
    maxLengthOverride != null && Number.isFinite(maxLengthOverride)
      ? maxLengthOverride
      : attr.maxLength;

  if (attr.key === 'material') {
    return (
      <MaterialControl
        attr={attr}
        currentValue={currentValue}
        onChange={onChange}
        disabledOptions={disabledOptions}
      />
    );
  }

  if (attr.key === 'gemstone') {
    return (
      <ColorControl
        attr={attr}
        gemstoneValue={currentValue}
        material={String(selections?.material ?? '')}
        onChange={onChange}
        disabledOptions={disabledOptions}
      />
    );
  }

  switch (attr.type) {
    case 'boolean':
      return <InlineToggle on={currentValue === 'true'} onChange={(v) => fire(v)} />;
    case 'text':
      return <InlineText value={currentValue} onChange={(v) => fire(v)} maxLength={maxLength} />;
    case 'number':
      return <InlineNumber value={Number(currentValue) || attr.min || 0} min={attr.min ?? 0} max={attr.max ?? 100} step={attr.step ?? 1} unit={attr.unit} onChange={(v) => fire(v)} />;
    default:
      return <InlineSelect values={vals} currentValue={currentValue} onChange={(v) => fire(v)} disabledOptions={disabledOptions} />;
  }
}

/* ------------------------------------------------------------------ */
/*  Panel                                                              */
/* ------------------------------------------------------------------ */

export function ConfigOptionsPanel({
  attributes,
  components,
  selections,
  onAttributeChange,
  onComponentSelect,
  onSummaryOpen,
  totalPrice,
  hasSelections,
  componentMetadata,
  disabledOptionsByAttr,
  maxLengthByAttr,
  mobileDocked = false,
  mobileCollapsed = false,
  onMobileCollapseToggle,
  desktopCollapsed = false,
  onDesktopCollapseToggle,
  experienceMode = 'free',
  guidedActiveId = null,
  guidedStepIndex = 0,
  guidedStepTotal = 0,
  onGuidedPrev,
  onGuidedNext,
  hideExportFooter = false,
}: ConfigOptionsPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeSectionRef = useRef<HTMLDivElement | null>(null);
  const isGuided = experienceMode === 'guided';

  const scrollActiveIntoView = useCallback(() => {
    if (!isGuided || !guidedActiveId || !activeSectionRef.current) return;
    activeSectionRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [isGuided, guidedActiveId]);

  useEffect(() => { scrollActiveIntoView(); }, [guidedActiveId, scrollActiveIntoView]);

  const optCount = attributes.length + components.length;
  const sectionMuted = (id: string) => isGuided && guidedActiveId !== null && guidedActiveId !== id;

  /* ---- mobile collapsed: thin tap-to-expand bar ---- */
  if (mobileDocked && mobileCollapsed && onMobileCollapseToggle) {
    return (
      <div id="config-options" role="region" aria-label="Configuration options" className="w-full">
        <button
          type="button"
          onClick={onMobileCollapseToggle}
          className="flex w-full items-center justify-center gap-2 px-5 py-3"
          aria-expanded={false}
        >
          <span className={`text-[11px] font-light tracking-widest ${MENU_MUTED}`} style={SERIF}>
            {optCount} option{optCount === 1 ? '' : 's'}
          </span>
          <svg className={`h-3 w-3 ${MENU_MUTED}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      </div>
    );
  }

  /* ---- desktop collapsed ---- */
  if (desktopCollapsed && onDesktopCollapseToggle) {
    return (
      <div id="config-options" role="region" aria-label="Configuration options" className="hidden lg:flex w-full flex-col">
        <button type="button" onClick={onDesktopCollapseToggle} className="flex w-full items-center justify-end gap-2 rounded-lg px-4 py-3 text-right transition-colors hover:bg-black/30" aria-expanded={false}>
          <span className={`text-[11px] font-light tracking-widest ${MENU_MUTED}`} style={SERIF}>Options</span>
          <svg className={`h-3 w-3 shrink-0 ${MENU_MUTED}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </button>
      </div>
    );
  }

  /* ---- open panel: NO box, NO background ---- */
  return (
    <div
      id="config-options"
      role="region"
      aria-label="Configuration options"
      className={`flex w-full min-h-0 flex-col lg:h-full lg:flex-1 ${mobileDocked ? 'max-lg:min-h-0 max-lg:flex-1' : ''}`}
    >
      {/* minimal header: guided nav + collapse — flush right with option stack */}
      <div className="flex shrink-0 items-center justify-end gap-3 px-5 pt-3 pb-1 lg:px-4">
        {isGuided && guidedStepTotal > 0 ? (
          <span className={`text-[9px] tabular-nums tracking-[0.25em] ${MENU_MUTED}`}>
            {guidedStepIndex + 1} / {guidedStepTotal}
          </span>
        ) : null}
        <div className="flex items-center gap-0.5">
          {isGuided && onGuidedPrev && onGuidedNext && guidedStepTotal > 1 && (
            <>
              <button type="button" aria-label="Previous" disabled={guidedStepIndex <= 0} onClick={onGuidedPrev}
                className={`flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/35 backdrop-blur-md ${MENU_MUTED} hover:border-white/35 hover:bg-black/50 disabled:opacity-20 transition-colors`}>
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
              </button>
              <button type="button" aria-label="Next" disabled={guidedStepIndex >= guidedStepTotal - 1} onClick={onGuidedNext}
                className={`flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/35 backdrop-blur-md ${MENU_MUTED} hover:border-white/35 hover:bg-black/50 disabled:opacity-20 transition-colors`}>
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
              </button>
            </>
          )}
          {onDesktopCollapseToggle && (
            <button type="button" onClick={onDesktopCollapseToggle} className={`hidden lg:flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/35 backdrop-blur-md ${MENU_MUTED} hover:border-white/35 transition-colors`} aria-label="Collapse">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </button>
          )}
          {mobileDocked && onMobileCollapseToggle && (
            <button type="button" onClick={onMobileCollapseToggle} className={`lg:hidden flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/35 backdrop-blur-md ${MENU_MUTED} hover:border-white/35 transition-colors`} aria-label="Minimize">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* scrollable attribute list (options only — overview is a separate footer) */}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-3 lg:px-4 scrollbar-none"
        style={{ scrollbarWidth: 'none' }}
      >
        {attributes.length === 0 && components.length === 0 && (
          <p className={`py-8 text-center text-[11px] font-light ${MENU_MUTED}`}>Nothing to configure.</p>
        )}

        {attributes.map((attr) => {
          const id = `attr:${attr.key}`;
          const muted = sectionMuted(id);
          const isActive = isGuided && guidedActiveId === id;
          const currentValue = selections[attr.key] != null
            ? String(selections[attr.key])
            : attr.defaultValue != null ? String(attr.defaultValue) : '';

          return (
            <div
              key={attr.key}
              ref={isActive ? activeSectionRef : undefined}
              className={`py-4 transition-all duration-500 ${muted ? 'opacity-15 pointer-events-none' : ''}`}
            >
              <h4 className={`mb-2.5 text-right text-[13px] font-extralight tracking-wide ${MENU_LABEL}`} style={SERIF}>
                {attr.label}
              </h4>

              {renderControl(
                attr,
                currentValue,
                onAttributeChange,
                disabledOptionsByAttr?.[attr.key],
                maxLengthByAttr?.[attr.key],
                selections,
              )}
            </div>
          );
        })}

        {components.map((comp) => {
          const id = `comp:${comp.id}`;
          const selectedVariantId = selections[`component:${comp.id}`] as string | undefined;
          const variants = comp.allowedVariantIds ?? [];
          const variantsMeta = componentMetadata?.[comp.id]?.variants as Record<string, { name?: string }> | undefined;
          const muted = sectionMuted(id);
          const isActive = isGuided && guidedActiveId === id;

          return (
            <div
              key={comp.id}
              ref={isActive ? activeSectionRef : undefined}
              className={`py-4 transition-all duration-500 ${muted ? 'opacity-15 pointer-events-none' : ''}`}
            >
              <h4 className={`mb-2.5 text-right text-[13px] font-extralight tracking-wide ${MENU_LABEL}`} style={SERIF}>
                {comp.label}
              </h4>
              {variants.length > 0 ? (
                <div className="flex flex-wrap justify-end gap-1.5" role="radiogroup" aria-label={comp.label}>
                  {variants.map((variantId) => {
                    const label = variantsMeta?.[variantId]?.name || variantId;
                    const sel = selectedVariantId === variantId;
                    return (
                      <button
                        key={variantId}
                        type="button"
                        role="radio"
                        aria-checked={sel}
                        onClick={() => onComponentSelect(comp.id, variantId)}
                        className={sel ? PILL_ON : PILL_IDLE}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className={`text-right text-[10px] ${MENU_MUTED}`}>No variants</p>
              )}
            </div>
          );
        })}
      </div>

      {(hasSelections || totalPrice) && !hideExportFooter && (
        <div className="shrink-0 px-5 pt-2 pb-4 text-right lg:px-4 lg:pb-3">
          <button
            type="button"
            onClick={onSummaryOpen}
            aria-label="Configuration, share and export"
            className={`text-right text-[11px] font-extralight tracking-wide transition-opacity hover:opacity-80 ${MENU_LABEL}`}
            style={SERIF}
          >
            {totalPrice ? <span className="mr-1">{totalPrice}</span> : null}
            Configuration & export
          </button>
        </div>
      )}
    </div>
  );
}
